import { Alert, Box, Button, Dialog, DialogContent, DialogTitle, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DynamicFormField from 'src/shared/components/form/dynamicFormField';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import NotesIcon from '@mui/icons-material/Notes';
import ArrowDropDownCircleOutlinedIcon from '@mui/icons-material/ArrowDropDownCircleOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline';
import PublicIcon from '@mui/icons-material/Public';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getFormSettingFields, getFormSettingPreview, updateFormSettingField, FormSettingField } from 'src/shared/services/memberFormSettingsService';
import { FormItemResponse } from 'src/modules/member/models/formMemberResponse';
import { MemberFormSettingEditModal } from './memberFormSettingEditModal';

type MemberFormConfiguratorProps = {
  formName: string;
};

const typeIcons: Record<number, JSX.Element> = {
  1: <TextFieldsIcon fontSize='small' />,
  2: <NotesIcon fontSize='small' />,
  4: <ArrowDropDownCircleOutlinedIcon fontSize='small' />,
  7: <CheckBoxOutlinedIcon fontSize='small' />,
  10: <PublicIcon fontSize='small' />,
  14: <TextFieldsIcon fontSize='small' />,
  21: <LabelOutlinedIcon fontSize='small' />,
  33: <ViewHeadlineIcon fontSize='small' />,
};

const typeLabels: Record<number, string> = {
  1: 'Text',
  2: 'Flerrad',
  3: 'Textblock',
  4: 'Dropdown',
  7: 'Kryssruta',
  10: 'Landsväljare',
  12: 'Kommun',
  13: 'Län',
  14: 'Postnr',
  15: 'Datum',
  21: 'Etikett',
  33: 'Rubrik',
};

const getAlertSuffix = (name: string): string => {
  if (name === 'other') return 'Other';
  if (name === 'mypagepersonal') return 'Mypage';
  return 'Member';
};

export const MemberFormConfigurator = ({ formName }: MemberFormConfiguratorProps) => {
  const { t } = useTranslation(['settings', 'common']);
  const { currentUser } = useGlobals();
  const otherLabel = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.OtherLabel, t('settings:memberRegistry.formOther'));
  const isMounted = useIsMounted();

  const [fields, setFields] = useState<FormSettingField[]>([]);
  const [previewItems, setPreviewItems] = useState<FormItemResponse[]>([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<FormSettingField | null>(null);
  const [dragField, setDragField] = useState<FormSettingField | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const fetchFields = useCallback(async () => {
    try {
      const [fieldsRes, previewRes] = await Promise.all([getFormSettingFields(formName), getFormSettingPreview(formName)]);
      if (isMounted()) {
        const associationFields = new Set(['association', 'countyAss_id', 'localAss_id']);
        setFields(fieldsRes.data.filter((f: FormSettingField) => !associationFields.has(f.fieldName)));
        setPreviewItems((previewRes.data.items || []).filter((item: FormItemResponse) => !associationFields.has(item.formKey)));
      }
    } catch (e) {
      ConsoleHelper.log(e);
    }
  }, [formName, isMounted]);

  useEffect(() => {
    fetchFields();
  }, [fetchFields]);

  const handleEdit = (field: FormSettingField) => {
    setEditingField(field);
    setEditModalOpen(true);
  };

  const handleModalSave = () => {
    setEditModalOpen(false);
    setEditingField(null);
    fetchFields();
  };

  const [dropTargetRow, setDropTargetRow] = useState<number | null>(null);

  const sortedFields = [...fields].sort((a, b) => a.row - b.row || a.formSettingsID - b.formSettingsID);

  // Flytta fält till en specifik rad och kolumn
  const handleDropOnRow = async (targetRow: number, targetCol?: number) => {
    if (!dragField) return;

    const col = targetCol ?? sortedFields.filter((f) => f.row === targetRow).length;

    if (dragField.row === targetRow && dragField.col === col) {
      setDragField(null);
      setDropTargetRow(null);
      return;
    }

    try {
      await updateFormSettingField(formName, dragField.formSettingsID, { ...dragField, row: targetRow, col });
      fetchFields();
    } catch (e) {
      ConsoleHelper.log(e);
    }
    setDragField(null);
    setDropTargetRow(null);
  };

  // Släpp på ett specifikt fält — byt plats
  const handleDropOnField = async (targetField: FormSettingField) => {
    if (!dragField || dragField.formSettingsID === targetField.formSettingsID) return;

    try {
      await Promise.all([
        updateFormSettingField(formName, dragField.formSettingsID, { ...dragField, row: targetField.row, col: targetField.col }),
        updateFormSettingField(formName, targetField.formSettingsID, { ...targetField, row: dragField.row, col: dragField.col }),
      ]);
      fetchFields();
    } catch (e) {
      ConsoleHelper.log(e);
    }
    setDragField(null);
    setDropTargetRow(null);
  };

  // Släpp på ny rad (under alla befintliga)
  const handleDropOnNewRow = async () => {
    if (!dragField) return;
    const maxRow = sortedFields.reduce((max, f) => Math.max(max, f.row), 0);
    await handleDropOnRow(maxRow + 1, 0);
  };

  const handleResize = async (field: FormSettingField, delta: number) => {
    const newSize = Math.max(1, Math.min(4, field.fieldSize + delta));
    if (newSize === field.fieldSize) return;
    try {
      await updateFormSettingField(formName, field.formSettingsID, { ...field, fieldSize: newSize });
      fetchFields();
    } catch (e) {
      ConsoleHelper.log(e);
    }
  };

  const getTypeIcon = (field: FormSettingField) => {
    // Profilfält med type 1 renderas som checkbox (samma override som GetFormResponse)
    const isProfileField = field.fieldName.startsWith('profileSpecial') || field.fieldName === 'profile' || field.fieldName === 'profileHead';
    const isHeadline = field.fieldName.startsWith('headline');
    const isTextField = field.fieldName.startsWith('text');
    let displayType = field.type;
    if (isProfileField && field.type === 1) displayType = 7;
    if (isHeadline) displayType = 33;
    if (isTextField) displayType = 3;
    const icon = typeIcons[displayType] || <TextFieldsIcon fontSize='small' />;
    return (
      <Tooltip title={typeLabels[displayType] || String(displayType)} arrow>
        <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>{icon}</Box>
      </Tooltip>
    );
  };

  // Preview via DynamicFormField
  const previewFormMethods = useForm({
    defaultValues: { items: previewItems.map((item) => ({ value: item.value || item.formValue || '' })) },
  });

  const previewRows = previewItems.reduce((acc, item) => {
    const r = item.formSetting.row;
    if (!acc[r]) acc[r] = [];
    acc[r].push(item);
    return acc;
  }, {} as Record<number, FormItemResponse[]>);
  Object.values(previewRows).forEach((rowItems) => rowItems.sort((a, b) => a.formSetting.col - b.formSetting.col));

  return (
    <Grid container spacing={2} sx={{ p: 3 }}>
      <Grid item xs={12} md={4}>
        <Typography variant='subtitle1' fontWeight='bold'>
          {t('settings:memberRegistry.fieldList')}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {t('settings:memberRegistry.fieldListDescription')}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {'- '}
          {t('settings:memberRegistry.fieldListHint1')}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {'- '}
          {t('settings:memberRegistry.fieldListHint2')}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {'- '}
          {t('settings:memberRegistry.fieldListHint3')}
        </Typography>
        <Alert severity='info' sx={{ mt: 2 }}>
          {t(`settings:memberRegistry.fieldListAlert${getAlertSuffix(formName)}`, {
            otherLabel: otherLabel.toLowerCase(),
          })}
          <Box sx={{ mt: 1 }}>
            <Button variant='outlined' size='small' startIcon={<VisibilityOutlinedIcon />} onClick={() => setPreviewOpen(true)}>
              {t('settings:memberRegistry.preview')}
            </Button>
          </Box>
        </Alert>
      </Grid>
      <Grid item xs={12} md={8}>
        {/* Kolumnrutnät */}
        <Grid container spacing={0.5} sx={{ mb: 1 }}>
          {[1, 2, 3, 4].map((col) => (
            <Grid item xs={3} key={col}>
              <Box sx={{ bgcolor: 'grey.100', borderRadius: 0.5, py: 0.25, textAlign: 'center' }}>
                <Typography variant='caption' color='text.disabled'>
                  {col}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {(() => {
          const visibleSorted = sortedFields.filter((f) => f.show);
          const hiddenSorted = sortedFields.filter((f) => !f.show);
          // Gruppera synliga fält per rad
          const fieldRows = visibleSorted.reduce((acc, field) => {
            const r = field.row;
            if (!acc[r]) acc[r] = [];
            acc[r].push(field);
            return acc;
          }, {} as Record<number, FormSettingField[]>);
          Object.values(fieldRows).forEach((rowFields) => rowFields.sort((a, b) => a.col - b.col));

          const isHeadlineBox = (f: FormSettingField) => f.fieldName.startsWith('headline');
          const hideIcon = (f: FormSettingField) => isHeadlineBox(f) || f.fieldName.startsWith('text');

          const renderFieldBox = (field: FormSettingField) => (
            <Box
              key={field.formSettingsID}
              draggable
              onDragStart={(e) => {
                setDragField(field);
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.stopPropagation();
                handleDropOnField(field);
              }}
              display='flex'
              gap={0.5}
              px={1}
              py={0.5}
              alignItems='center'
              sx={{
                bgcolor: dragField?.formSettingsID === field.formSettingsID ? 'primary.50' : '#fff',
                border: 1,
                borderColor: 'primary.200',
                borderRadius: 1,
                opacity: field.show ? 1 : 0.5,
                minWidth: 0,
                cursor: 'grab',
                '&:active': { cursor: 'grabbing' },
              }}
            >
              <DragIndicatorIcon fontSize='small' sx={{ color: 'text.disabled', mr: -0.5 }} />
              {!hideIcon(field) && getTypeIcon(field)}
              <Typography variant='body2' sx={{ flex: 1, minWidth: 0, fontWeight: isHeadlineBox(field) ? 600 : 400 }} noWrap>
                {field.heading || field.fieldName}
                {field.required && (
                  <Typography component='span' variant='body2' color='error.main' sx={{ fontWeight: 700, ml: 0.5 }}>
                    *
                  </Typography>
                )}
              </Typography>
              <Tooltip title={t('settings:memberRegistry.decreaseWidth')} arrow>
                <span>
                  <IconButton
                    size='small'
                    onClick={() => handleResize(field, -1)}
                    disabled={field.fieldSize <= 1}
                    sx={{ p: 0, width: 20, height: 20 }}
                  >
                    <RemoveIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('settings:memberRegistry.increaseWidth')} arrow>
                <span>
                  <IconButton
                    size='small'
                    onClick={() => handleResize(field, 1)}
                    disabled={field.fieldSize >= 4}
                    sx={{ p: 0, width: 20, height: 20 }}
                  >
                    <AddIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton size='small' onClick={() => handleEdit(field)}>
                <EditOutlinedIcon fontSize='small' />
              </IconButton>
            </Box>
          );

          const rowNumbers = Object.keys(fieldRows)
            .map(Number)
            .sort((a, b) => a - b);

          return (
            <>
              {rowNumbers.map((rowNum) => {
                const rowFields = fieldRows[rowNum];
                return (
                  <Box
                    key={rowNum}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDropTargetRow(rowNum);
                    }}
                    onDragLeave={() => setDropTargetRow(null)}
                    onDrop={() => handleDropOnRow(rowNum)}
                    sx={{
                      mb: 0.5,
                      p: 0.25,
                      borderRadius: 1,
                      border: dropTargetRow === rowNum && dragField ? '2px dashed' : '2px solid transparent',
                      borderColor: dropTargetRow === rowNum && dragField ? 'primary.main' : 'transparent',
                      position: 'relative',
                    }}
                  >
                    {/* Kolumnbakgrund */}
                    <Grid container spacing={0.5} sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', p: 0.25 }}>
                      {[0, 1, 2, 3].map((col) => (
                        <Grid item xs={3} key={col}>
                          <Box sx={{ bgcolor: 'grey.50', borderRadius: 0.5, height: '100%' }} />
                        </Grid>
                      ))}
                    </Grid>
                    <Grid container spacing={0.5} sx={{ position: 'relative' }}>
                      {rowFields.map((field) => (
                        <Grid item xs={12} sm={field.fieldSize > 1 ? 12 : 6} md={(field.fieldSize * 3) as 3 | 6 | 9 | 12} key={field.formSettingsID}>
                          {renderFieldBox(field)}
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                );
              })}

              {/* Drop-zon för ny rad */}
              {dragField && (
                <Box
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDropTargetRow(-1);
                  }}
                  onDragLeave={() => setDropTargetRow(null)}
                  onDrop={handleDropOnNewRow}
                  sx={{
                    mt: 0.5,
                    p: 1.5,
                    borderRadius: 1,
                    border: '2px dashed',
                    borderColor: dropTargetRow === -1 ? 'primary.main' : 'grey.300',
                    bgcolor: dropTargetRow === -1 ? 'primary.50' : 'transparent',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant='caption' color='text.secondary'>
                    {t('settings:memberRegistry.dropNewRow')}
                  </Typography>
                </Box>
              )}

              {hiddenSorted.length > 0 && (
                <Typography variant='caption' color='text.secondary' sx={{ mt: 1, mb: 0.5, display: 'block' }}>
                  {t('settings:memberRegistry.hiddenFields')}
                </Typography>
              )}
              {hiddenSorted.map((field) => (
                <Box key={field.formSettingsID} sx={{ mb: 0.5 }}>
                  {renderFieldBox(field)}
                </Box>
              ))}
            </>
          );
        })()}
      </Grid>

      {/* Förhandsgransknings-modal */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth='md' fullWidth>
        <DialogTitle>{t('settings:memberRegistry.preview')}</DialogTitle>
        <DialogContent>
          <FormProvider {...previewFormMethods}>
            <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 2, '& .MuiInputBase-root': { bgcolor: '#fff' } }}>
              {Object.entries(previewRows).map(([row, rowItems]) => (
                <Box sx={{ mb: 1 }} key={row}>
                  <GridWrapper>
                    {rowItems.map((item) => {
                      const idx = previewItems.indexOf(item);
                      const safeItem = { ...item, value: item.value ?? item.formValue ?? '' };
                      return (
                        <DynamicFormField key={`${item.formKey}-${item.formSetting.row}-${item.formSetting.col}`} formItem={safeItem} index={idx} />
                      );
                    })}
                  </GridWrapper>
                </Box>
              ))}
            </Box>
          </FormProvider>
        </DialogContent>
      </Dialog>

      {/* Redigera-modal */}
      {editModalOpen && (
        <MemberFormSettingEditModal
          formName={formName}
          field={editingField}
          usedFieldNames={fields.map((f) => f.fieldName)}
          onSave={handleModalSave}
          onClose={() => {
            setEditModalOpen(false);
            setEditingField(null);
          }}
        />
      )}
    </Grid>
  );
};
